import {
  invalidateTaskCaches,
  taskKeys,
  useAllTasks,
  useTask,
} from '@/features/tasks/model/useTaskActions';
import { useAuthStore } from '@/store/auth';
import { useTaskSelectionModalStore } from '@/entities/task/model/stores/task-selection-modal';
import { useTaskTimerStore } from '@/entities/task/model/stores/task-timer';
import { useEffect, useMemo, useState } from 'react';
import { useUsers } from '@/hooks/users/useUserActions';
import { createComment } from '@/entities/task/api/comments';
import { completeTask } from '@/entities/task/api/tasks';
import {
  adjustIntermediatePlan,
  completeIntermediate,
  intermediateErrorMessage,
  resumeIntermediate,
  useIntermediateStore,
} from '@/entities/intermediate';
import { addChatResult } from '@/entities/task/api/chat';
import { disconnectJumpSessions } from '@/lib/api/toJump';
import { useRenewTask } from '@/features/tasks/mobile-calendar/hooks/useRenewTask';
import { useQueryClient } from '@tanstack/react-query';
import { endOfWeek, isWithinInterval, parseISO, startOfWeek } from 'date-fns';
import { toast } from 'sonner';
import { elapsedTimeKeys } from '../elapsedTimeKeys';
import { useIntermediateTask } from '../useIntermediateTask';
import { useBreakWatcher } from './useBreakWatcher';

type TaskTab = 'today' | 'tomorrow' | 'week' | 'deferred' | 'all';

function isTaskHidden(task: ANY): boolean {
  return (
    task?.status === 'done' ||
    task?.status === 'waiting_review' ||
    task?.STATUS === '5' ||
    task?.backendStatus === 5
  );
}

async function safeDisconnectJump(taskId: string | number | null | undefined) {
  if (!taskId) {
    return;
  }

  try {
    await disconnectJumpSessions({ task_id: taskId });
  } catch (e) {
    console.error('disconnectJumpSessions failed', e);
  }
}

function toDueMs(dueDate: unknown): number | null {
  if (!dueDate) {
    return null;
  }

  const d =
    typeof dueDate === 'string'
      ? parseISO(dueDate)
      : dueDate instanceof Date
        ? dueDate
        : new Date(String(dueDate));

  const ms = d.getTime();
  if (Number.isNaN(ms)) {
    return null;
  }

  return ms;
}

function pickNextTaskToday(
  tasks: ANY[],
  nowMs: number,
  excludeTaskId?: string | number | null
) {
  const upcoming = tasks
    .filter((t) => {
      if (!t?.dueDate) {
        return false;
      }
      if (excludeTaskId != null && String(t.id) === String(excludeTaskId)) {
        return false;
      }

      const dueMs = toDueMs(t.dueDate);
      if (dueMs == null) {
        return false;
      }

      return dueMs >= nowMs;
    })
    .map((t) => ({ t, dueMs: toDueMs(t.dueDate)! }))
    .sort((a, b) => a.dueMs - b.dueMs);

  return upcoming[0]?.t ?? null;
}

export function useTaskSelectionModalLogic() {
  const [nowMs] = useState(() => Date.now());
  const { renewTask, isLoading: isRenewing } = useRenewTask();
  const { userId } = useAuthStore();
  const { isOpen, closeModal, mode, targetTaskId, openModal } =
    useTaskSelectionModalStore();

  const { data: allTasks = [] } = useAllTasks();
  const { startTask, getCurrentElapsed, pauseTask } = useTaskTimerStore();

  // Промежуточное дело по таймауту: результат (что сделал) — обязателен,
  // действия заблокированы, пока поле не заполнено.
  const isIntermediateTimeup = mode === 'intermediate-timeup';

  const queryClient = useQueryClient();

  const { startWatching } = useBreakWatcher();

  const [activeTab, setActiveTab] = useState<TaskTab>('today');
  const [pauseComment, setPauseComment] = useState('');
  const [breakMinutes, setBreakMinutes] = useState<number>(15);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const pausedTaskId = targetTaskId ?? useTaskTimerStore.getState().lastTaskId;
  const { data: pausedTask } = useTask(pausedTaskId ?? '');

  const isSaveDisabled = breakMinutes <= 0 || !pausedTaskId;

  const activeTaskId = useTaskTimerStore((s) => s.activeTaskId);

  const handleBreak = async (minutes = 15) => {
    if (isIntermediateTimeup && !pauseComment.trim()) {
      toast.error('Укажите, что вы сделали за это время');
      return;
    }

    setBreakMinutes(minutes);

    if (isIntermediateTimeup) {
      await finalizeIntermediate();
    } else {
      await flushWithComment('break');
    }

    const endsAt = Date.now() + minutes * 60_000;
    startWatching(endsAt);

    toast.success(
      mode === 'complete'
        ? 'Перерыв начался после завершения задачи'
        : 'Перерыв начался',
      { description: `Напомню через ${minutes} мин` }
    );

    setPauseComment('');
    closeModal();
  };

  const handleResumeTask = async () => {
    if (!pausedTaskId || isSwitching) {
      return;
    }

    // Промежуточное дело: «Вернуться в завершённую задачу» — фиксируем результат
    // и завершаем (возобновлять промежуточное дело нельзя без результата).
    if (isIntermediateTimeup) {
      if (!pauseComment.trim()) {
        toast.error('Укажите, что вы сделали за это время');
        return;
      }
      setIsSwitching(true);
      try {
        await finalizeIntermediate();
        setPauseComment('');
        closeModal();
      } finally {
        setIsSwitching(false);
      }
      return;
    }

    setIsSwitching(true);
    try {
      await renewTask(pausedTaskId);

      queryClient.invalidateQueries({
        queryKey: ['task', String(pausedTaskId)],
      });

      setPauseComment('');
      closeModal();
    } finally {
      setIsSwitching(false);
    }
  };

  async function flushCurrentTask(params?: {
    breakMin?: number;
    comment?: string;
  }) {
    if (!pausedTaskId) {
      return;
    }

    const comment = params?.comment?.trim();

    if (comment) {
      await createComment(String(pausedTaskId), { POST_MESSAGE: comment });
    }

    await safeDisconnectJump(pausedTaskId);

    useTaskTimerStore.setState((s: ANY) => ({
      ...s,
      tasks: (s.tasks ?? []).map((t: ANY) =>
        String(t.taskId) === String(pausedTaskId)
          ? { ...t, startTime: null, isRunning: false }
          : t
      ),
    }));

    await queryClient.invalidateQueries({
      queryKey: elapsedTimeKeys.byTask(pausedTaskId),
    });
    await queryClient.invalidateQueries({ queryKey: ['task', pausedTaskId] });
  }

  type FlushKind = 'break' | 'intermediate' | 'switch';

  function safeTitle(t: ANY): string {
    const v = t?.title;
    return typeof v === 'string' && v.trim() ? v.trim() : 'без названия';
  }

  const flushWithComment = async (kind: FlushKind, nextTask?: ANY) => {
    if (!pausedTaskId) {
      return;
    }

    const base = pauseComment.trim();

    let note = '';
    if (kind === 'break') {
      note =
        mode === 'complete'
          ? `Действие: завершил #${pausedTaskId} → перерыв`
          : `Действие: пауза #${pausedTaskId} → перерыв`;
    }

    if (kind === 'intermediate') {
      note =
        mode === 'complete'
          ? `Действие: завершил #${pausedTaskId} → промежуточная задача`
          : `Действие: пауза #${pausedTaskId} → промежуточная задача`;
    }

    if (kind === 'switch' && nextTask) {
      note =
        mode === 'complete'
          ? `Переключение: завершил #${pausedTaskId} → продолжил #${nextTask.id} (${safeTitle(nextTask)})`
          : `Переключение: поставил на паузу #${pausedTaskId} → продолжил #${nextTask.id} (${safeTitle(nextTask)})`;
    }

    const finalComment = base
      ? `Причина: ${base}\n${note}`.trim()
      : note.trim();

    await flushCurrentTask({ breakMin: 0, comment: finalComment });
  };

  // Завершение промежуточного дела по таймауту: текст из поля фиксируется как
  // результат задачи, дело завершается через серверную систему трекинга
  // (флаш времени + закрытие в Bitrix). При сбое дело остаётся paused, время
  // не теряется — показываем ошибку, а не тихий успех.
  const finalizeIntermediate = async () => {
    if (!pausedTaskId) {
      return;
    }
    const result = pauseComment.trim();
    if (!result) {
      return;
    }

    await addChatResult(String(pausedTaskId), result);
    try {
      const state = await completeIntermediate(Number(pausedTaskId));
      useIntermediateStore.getState().upsert(state);
    } catch (e) {
      console.error('completeIntermediate failed', e);
      toast.error(intermediateErrorMessage(e));
    }
    await safeDisconnectJump(pausedTaskId);

    await invalidateTaskCaches(queryClient);
  };

  // Промежуточное дело по таймауту: «продлить план» — сдвигаем план/дедлайн
  // на бэке (PATCH /time) и возобновляем дело; результат при этом не требуем —
  // работа продолжается.
  const handleExtendIntermediate = async (minutes = 5) => {
    if (!isIntermediateTimeup || !pausedTaskId) {
      return;
    }
    try {
      await adjustIntermediatePlan(Number(pausedTaskId), minutes);
      const state = await resumeIntermediate(Number(pausedTaskId));
      useIntermediateStore.getState().upsert(state);
      toast.success(`План продлён на ${minutes} мин, дело продолжается`);
      setPauseComment('');
      closeModal();
    } catch (e) {
      console.error('extend intermediate failed', e);
      toast.error(intermediateErrorMessage(e));
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) {
      return;
    }
    setIsRefreshing(true);

    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: taskKeys.all }),
        pausedTaskId
          ? queryClient.invalidateQueries({ queryKey: ['task', pausedTaskId] })
          : Promise.resolve(),
        pausedTaskId
          ? queryClient.invalidateQueries({
              queryKey: elapsedTimeKeys.byTask(pausedTaskId),
            })
          : Promise.resolve(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSave = async () => {
    if (!pausedTaskId) {
      return;
    }

    if (String(activeTaskId ?? '') === String(pausedTaskId)) {
      await pauseTask(pausedTaskId, breakMinutes);
    } else {
      await queryClient.invalidateQueries({
        queryKey: elapsedTimeKeys.byTask(pausedTaskId),
      });
      await queryClient.invalidateQueries({ queryKey: ['task', pausedTaskId] });
    }

    if (pauseComment.trim()) {
      console.warn(
        'Pause comment not sent (backend handles elapsed):',
        pauseComment.trim()
      );
    }

    await safeDisconnectJump(pausedTaskId);

    useTaskTimerStore.setState((s: ANY) => ({
      ...s,
      tasks: (s.tasks ?? []).map((t: ANY) =>
        String(t.taskId) === String(pausedTaskId)
          ? { ...t, startTime: null, isRunning: false }
          : t
      ),
    }));

    await queryClient.invalidateQueries({
      queryKey: elapsedTimeKeys.byTask(pausedTaskId),
    });
    await queryClient.invalidateQueries({ queryKey: ['task', pausedTaskId] });

    if (mode === 'complete') {
      await completeTask(pausedTaskId);
      await safeDisconnectJump(pausedTaskId);

      useTaskTimerStore.setState({ activeTaskId: null, lastTaskId: null });
      toast.success('Задача завершена');
    }

    // Обновляем списки/доски: и при паузе, и при завершении статус меняется.
    await invalidateTaskCaches(queryClient);

    const endsAt = Date.now() + breakMinutes * 60_000;
    startWatching(endsAt);

    toast.success(
      mode === 'complete'
        ? 'Перерыв начался после завершения задачи'
        : 'Перерыв начался',
      { description: `Напомню через ${breakMinutes} мин` }
    );

    setPauseComment('');
    closeModal();
  };

  const handleCompleteAndSwitchTo = async (nextTask: ANY) => {
    if (!pausedTaskId) {
      return;
    }

    const switchNote = `Переключение: завершил #${pausedTaskId} → продолжил #${nextTask?.id} (${nextTask?.title ?? 'без названия'})`;
    const base = pauseComment.trim();
    const finalComment = base
      ? `Причина переключения: ${base}\n${switchNote}`
      : switchNote;

    if (finalComment.trim()) {
      await flushCurrentTask({ breakMin: 0, comment: finalComment });
    }

    await safeDisconnectJump(pausedTaskId);

    useTaskTimerStore.setState((s: ANY) => ({
      ...s,
      tasks: (s.tasks ?? []).map((t: ANY) =>
        String(t.taskId) === String(pausedTaskId)
          ? { ...t, startTime: null, isRunning: false }
          : t
      ),
    }));

    await queryClient.invalidateQueries({
      queryKey: elapsedTimeKeys.byTask(pausedTaskId),
    });

    await safeDisconnectJump(pausedTaskId);

    useTaskTimerStore.setState({ activeTaskId: null, lastTaskId: null });

    await invalidateTaskCaches(queryClient);

    await startTask(nextTask.id.toString(), {
      title: nextTask.title,
      plannedMinutes: nextTask.timeEstimate
        ? Math.floor(nextTask.timeEstimate / 60)
        : 0,
    });

    toast.success('Задача завершена и переключено на другую', {
      description: nextTask.title,
    });

    setPauseComment('');
    closeModal();
  };

  const getTasksByTab = (): Record<TaskTab, ANY[]> => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });

    const filtered = allTasks.filter((t: ANY) => {
      if (t.assigneeId !== userId) {
        return false;
      }
      return !isTaskHidden(t);
    });

    return {
      today: filtered.filter((t) => {
        if (!t.dueDate) {
          return false;
        }

        const d = new Date(t.dueDate);
        d.setHours(0, 0, 0, 0);

        return d.getTime() === today.getTime();
      }),

      tomorrow: filtered.filter((t) => {
        if (!t.dueDate) {
          return false;
        }

        const d = new Date(t.dueDate);
        d.setHours(0, 0, 0, 0);

        return d.getTime() === tomorrow.getTime();
      }),

      week: filtered.filter((t) => {
        if (!t.dueDate) {
          return false;
        }

        const d =
          typeof t.dueDate === 'string'
            ? parseISO(t.dueDate)
            : new Date(t.dueDate);

        return isWithinInterval(d, { start: weekStart, end: weekEnd });
      }),
      deferred: filtered.filter((t) => !t.dueDate && !(t as ANY).isMeeting),

      all: filtered.filter((t) => (t as ANY).isMeeting === true),
    };
  };

  const tasksByTab = useMemo(getTasksByTab, [allTasks, userId]);

  // Промежуточное дело: факт/план берём из серверного состояния трекинга
  // (в общем таймере задач такие дела больше не живут).
  const intermediateItem = useIntermediateStore((s) =>
    isIntermediateTimeup && pausedTaskId
      ? s.items.find((it) => String(it.taskId) === String(pausedTaskId))
      : undefined
  );

  const spentMs = intermediateItem
    ? Math.max(0, intermediateItem.elapsedSeconds) * 1000
    : pausedTask?.id != null
      ? getCurrentElapsed(pausedTask.id)
      : 0;

  const plannedMs = intermediateItem
    ? intermediateItem.plannedSeconds * 1000
    : pausedTask?.timeEstimate
      ? pausedTask.timeEstimate * 1000
      : 0;

  const nextTodayTask = useMemo(() => {
    return pickNextTaskToday(tasksByTab.today ?? [], nowMs, pausedTaskId);
  }, [tasksByTab.today, nowMs, pausedTaskId]);

  const handleStartFromCard = async (task: ANY) => {
    if (!task?.id || isSwitching) {
      return;
    }
    if (isIntermediateTimeup && !pauseComment.trim()) {
      toast.error('Укажите, что вы сделали за это время');
      return;
    }

    setIsSwitching(true);

    try {
      if (isIntermediateTimeup) {
        await finalizeIntermediate();
      } else {
        await flushWithComment('switch', task);
      }

      await startTask(String(task.id), {
        title: task.title,
        plannedMinutes: task.timeEstimate
          ? Math.floor(task.timeEstimate / 60)
          : 0,
      });

      setPauseComment('');
      closeModal();
      toast.success('Задача началась', { description: task.title });
    } finally {
      setIsSwitching(false);
    }
  };

  const handleCompleteFromCard = (taskId: string) => {
    closeModal();
    openModal({ mode: 'complete', taskId });
  };

  const userIds = useMemo(() => {
    return Array.from(
      new Set(
        tasksByTab[activeTab]
          .flatMap((task) => [task.assigneeId, task.createdBy])
          .filter((id): id is number => typeof id === 'number')
      )
    );
  }, [tasksByTab, activeTab]);

  const { data: users = [] } = useUsers.useByIds(userIds);

  const usersMap = useMemo(() => {
    const map = new Map<number, ANY>();
    users.forEach((u) => map.set(u.ID, u));
    return map;
  }, [users]);

  const assigneeId = pausedTask?.assigneeId;
  const { data: assignee } = useUsers.useById(assigneeId ?? null);

  const { data: createdByUser } = useUsers.useById(
    pausedTask?.createdBy ?? null
  );

  const activePreviewImage = useMemo(() => {
    const files = pausedTask?.files;
    if (!Array.isArray(files)) {
      return undefined;
    }
    return files.find((f) => /\.(png|jpe?g|webp|gif|svg)$/i.test(f.name));
  }, [pausedTask]);

  const { createIntermediateTask, isLoading } = useIntermediateTask({
    type: 'intermediate',
  });

  const isBusy = Boolean(
    isSwitching || isRefreshing || isRenewing || isLoading
  );

  const handleCreateIntermediate = async () => {
    if (!userId) {
      return;
    }
    if (isSwitching) {
      return;
    }

    if (isIntermediateTimeup && !pauseComment.trim()) {
      toast.error('Укажите, что вы сделали за это время');
      return;
    }

    if (isIntermediateTimeup) {
      await finalizeIntermediate();
    } else {
      await flushWithComment('intermediate');
    }

    await createIntermediateTask('Промежуточная задача', undefined, userId);

    setPauseComment('');
    closeModal();
  };

  const [quickCreateType, setQuickCreateType] = useState<
    'intermediate' | 'meeting' | null
  >(null);

  useEffect(() => {
    if (!isOpen) {
      setQuickCreateType(null);
    }
  }, [isOpen]);

  return {
    isOpen,
    mode,
    quickCreateType,
    isBusy,

    isIntermediateTimeup,
    canActIntermediate: !isIntermediateTimeup || pauseComment.trim().length > 0,

    pausedTask,
    nextTodayTask,
    assignee,
    createdByUser,
    activePreviewImage,
    tasksByTab,
    usersMap,

    activeTab,
    pauseComment,
    breakMinutes,
    isRefreshing,
    spentMs,
    plannedMs,
    isSaveDisabled,

    setActiveTab,
    setPauseComment,
    setBreakMinutes,

    handleSave,
    handleRefresh,
    handleStartFromCard,
    handleCompleteFromCard,
    handleCreateIntermediate,
    handleExtendIntermediate,
    handleCompleteAndSwitchTo,
    closeModal,
    handleResumeTask,
    isRenewing,
    handleBreak,
  };
}
